import React, { useEffect, useState, useRef } from 'react'
import * as d3 from 'd3'
import { Wrapper } from './style'

const D3Range = ({ rangedata, onChange, selectedObject }) => {
  const svgRef = useRef()
  const [width, setWidth] = useState(0)

  useEffect(() => {
    const updateWidth = () => {
      if (svgRef.current) {
        setWidth(svgRef.current.parentNode.offsetWidth)
      }
    }
    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  const createRangeGraph = () => {
    const margin = { top: 20, right: 24, bottom: 0, left: 24 }
    const graphHeight = 40
    const svgWidth = width
    const svgHeight = 60

    const rawData = rangedata || []
    // [
    //   { date: '2023-09-25', value: 21 }, { date: '2023-10-06', value: 12 },
    //   { date: '2024-09-10', value: 11 }, { date: '2024-10-08', value: 16 },
    //   { date: '2024-11-09', value: 20 }, { date: '2024-12-07', value: 33 },
    //   { date: '2025-01-10', value: 35 }, { date: '2025-02-06', value: 37 },
    //   { date: '2025-03-07', value: 37 }, { date: '2025-04-04', value: 42 }
    // ];

    const data = rawData.map((d, i) => {
      const date = new Date(d.date)
      const showLabel =
        i === 0 || new Date(rawData[i - 1].date).getFullYear() !== date.getFullYear()
      return { ...d, date, index: i, showLabel }
    })

    const svg = d3
      .select(svgRef.current)
      .attr('width', svgWidth)
      .attr('height', svgHeight)
      .style('background-color', '#F6F6F5')

    svg.selectAll('*').remove()

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`)

    const widthG = svgWidth - margin.left - margin.right

    const xBand = d3
      .scaleBand()
      .domain(data.map((d) => d.index))
      .range([0, widthG])
      .paddingInner(0.2)

    const y = d3
      .scaleLinear()
      .domain([0, d3.max(data, (d) => d.value)])
      .range([graphHeight, 0])

    const barWidth = 6

    // Vertical lines
    g.selectAll('.line')
      .data(data.filter((d) => d.showLabel))
      .enter()
      .append('line')
      .attr('class', 'line')
      .attr('x1', (d) => xBand(d.index) + barWidth / 2)
      .attr('x2', (d) => xBand(d.index) + barWidth / 2)
      .attr('y1', -20)
      .attr('y2', graphHeight)
      .attr('stroke', '#E7E7E6')
      .attr('stroke-width', 1)

    // Labels
    g.selectAll('.label')
      .data(data.filter((d) => d.showLabel))
      .enter()
      .append('text')
      .attr('class', 'label')
      .attr('x', (d) => xBand(d.index) + 3)
      .attr('y', -6)
      .attr('font-size', '11px')
      .attr('font-weight', '300')
      .attr('fill', '#ADADAC')
      .text((d) => d.date.getFullYear())

    const bars = g
      .selectAll('.bar')
      .data(data)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', (d) => xBand(d.index))
      .attr('y', (d) => y(d.value))
      .attr('width', barWidth)
      .attr('height', (d) => graphHeight - y(d.value))
      .attr('fill', '#E7E7E6')
      .attr('opacity', 1)

    function brushed(event) {
      if (!event.selection) return

      const [x0, x1] = event.selection
      bars.attr('opacity', (d) => {
        const center = xBand(d.index) + barWidth / 2
        return center >= x0 && center <= x1 ? 1 : 1
      })
    }

    function brushEnded(event) {
      if (!event.selection) return

      const [x0, x1] = event.selection
      const selected = data.filter((d) => {
        const center = xBand(d.index) + barWidth / 2
        return center >= x0 && center <= x1
      })

      if (selected.length) {
        const startDate = selected[0].date.toISOString().slice(0, 10)
        const endDate = selected[selected.length - 1].date.toISOString().slice(0, 10)
        const timeStampStart = new Date(startDate).getTime()
        const timeStampEnd = new Date(endDate).getTime()
        onChange({ start: timeStampStart, end: timeStampEnd })
      }
    }

    const brush = d3
      .brushX()
      .extent([
        [0, -20],
        [widthG, graphHeight],
      ])
      .on('brush', brushed)
      .on('end', brushEnded)

    const brushG = g.append('g').attr('class', 'brush').call(brush)

    brushG
      .selectAll('.handle')
      .attr('width', 12)
      .attr('height', 24)
      .attr('fill', '#D8D8D6')
      .attr('rx', 0)
      .attr('ry', 0)

    brushG.select('.overlay').style('pointer-events', 'none')

    const convertTimestampToDate = (timestamp) => {
      const date = new Date(timestamp)
      return (
        date.getFullYear() +
        '-' +
        (date.getMonth() + 1).toString().padStart(2, '0') +
        '-' +
        date.getDate().toString().padStart(2, '0')
      )
    }

    const startDate = { date: convertTimestampToDate(selectedObject.startDate) }
    const endDate = { date: convertTimestampToDate(selectedObject.endDate) }

    const startIndex = data.find((d) => d.date.toISOString().startsWith(startDate.date))?.index
    const endIndex = data.find((d) => d.date.toISOString().startsWith(endDate.date))?.index

    if (startIndex !== undefined && endIndex !== undefined) {
      const from = Math.min(startIndex, endIndex)
      const to = Math.max(startIndex, endIndex)
      brushG.call(brush.move, [xBand(from), xBand(to) + barWidth])
      brushed({ selection: [xBand(from), xBand(to) + barWidth] })
    }
  }

  useEffect(() => {
    if (selectedObject?.endDate > selectedObject?.startDate && width > 0) {
      createRangeGraph()
    }
  }, [selectedObject, width])

  return (
    <Wrapper>
      <svg ref={svgRef}></svg>
    </Wrapper>
  )
}

export default D3Range
